import React from "react";
import {
  IAcademicWizardConfigs,
  IWizardConfig,
  WizardMode,
  SubmitButtonColor,
} from "../../../../wizard/types";
import useAcademicQueriesSecondary from "../../../hooks/useAcademicQueriesSecondary";
import useSchoolLevelsQueries from "../../../hooks/useSchoolLevelsQueries";
import { createContentConfig } from "../create/createContentConfig";
import { viewContentConfig } from "../view/viewContentConfig";
import { editContentConfig } from "../edit/editContentConfig";
import { parseToFrontEndModel } from "../helpers/parseToFrontEndModel";
import { parseToBackEndModel } from "../helpers/parseToBackEndModel";
import { useSchoolId } from "../../../../school/hooks/useSchoolId";
import DeleteContent from "../../../../program/components/DeleteContent";
import deactivateFormValidator from "../../validators/deactivateFormValidator";
import DeactivateContent from "../../../../../components/DeactivateContent/DeactivateContent";
import { getDeleteButtonConfigs } from "../../../../program/helpers/helperFunctions";
import { ENTITY_NAMES } from "../../../../../constants";

export function isLevelHasAcademic(values) {
  const schoolLevels = values.injectedData.schoolLevels;
  const selectedSchoolLevel = schoolLevels.find(
    (level) => level.id === values.selectedSchoolLevel.id,
  );

  const grade =
    values.schoolLevels?.[values.selectedSchoolLevel.id]?.schoolGrade?.name || // todo correct this code line
    values.schoolLevels?.[values.selectedSchoolLevel.id]?.otherGrade;

  return (
    (values.selectedSchoolLevel.id !== "_3" &&
      selectedSchoolLevel.academics.length) ||
    (values.selectedSchoolLevel.id === "_3" &&
      selectedSchoolLevel.academics.find(
        (academic) => academic.grade === grade,
      ))
  );
}

async function getCreateInitialData(fetchAllWithAcademic, schoolId) {
  const { data: schoolLevels } = await fetchAllWithAcademic(schoolId);

  schoolLevels.forEach((level) => {
    level.id = "_" + level.id;
  });

  const formSchoolLevels = parseToFrontEndModel(
    schoolLevels,
    WizardMode.Create,
  );
  const selectedSchoolLevel =
    schoolLevels.find((schoolLevel) => !schoolLevel.academics.length) ||
    schoolLevels.find((schoolLevel) => schoolLevel.id === "_3");

  return {
    injectedData: {
      schoolLevels,
    },
    selectedSchoolLevel,
    schoolLevels: formSchoolLevels,
  };
}

async function saveAcademic(data, createAcademic, schoolId) {
  if (isLevelHasAcademic(data)) {
    return null;
  }

  const parsedData = parseToBackEndModel(data, schoolId);

  return await createAcademic(parsedData);
}

function getButtonConfigs(values) {
  if (isLevelHasAcademic(values)) {
    // make view mode for existing degree
    return {
      submitButton: null,
      cancelButton: {
        text: "Close",
      },
    };
  }

  return {
    submitButton: {
      text: "Save",
      color: SubmitButtonColor.Success,
    },
    cancelButton: {
      text: "Close",
    },
  };
}

async function saveEditedAcademic(data, updateAcademic, schoolId, academicId) {
  const parsedData = parseToBackEndModel(data, schoolId, academicId);

  return await updateAcademic(parsedData);
}

async function getViewData(fetchOne, academicId) {
  const { data: data } = await fetchOne(academicId);

  return {
    injectedData: { data },
  };
}

async function getAcademicDataForEdit(fetchOne, academicId) {
  const { data: data } = await fetchOne(academicId);

  const schoolLevel = {
    id: "_" + data.levelId,
    name: data.levelName,
    academics: [
      {
        id: data.id,
        grade: data.grade,
        schoolType: data.schoolType,
        schoolTypeName: data.schoolTypeName,
        requiredDocs: data.requiredDocs,
        applicationForms: data.applicationForms,
        applicationLink: data?.applicationLink,
      },
    ],
  };

  const schoolLevels = [schoolLevel];
  const formSchoolLevels = parseToFrontEndModel(schoolLevels, WizardMode.Edit);

  return {
    injectedData: {
      schoolLevels,
    },
    selectedSchoolLevel: schoolLevel,
    schoolLevels: formSchoolLevels,
  };
}

async function getAppCount(fetch, id) {
  const { data } = await fetch({ academicId: id });

  return {
    injectedData: {
      approved: data.approved,
      pending: data.pending,
    },
  };
}

export function useAcademicConfig(): IAcademicWizardConfigs {
  const {
    create,
    update,
    fetchOne,
    activate,
    deactivate,
    deleteOne,
    fetchAppCount,
  } = useAcademicQueriesSecondary();
  const { fetchAllWithAcademic } = useSchoolLevelsQueries();
  const schoolId = useSchoolId();

  const getCreateConfig = (): IWizardConfig => ({
    steps: [createContentConfig],
    submitButtonText: "Save",
    submitButtonColor: SubmitButtonColor.Success,
    mode: WizardMode.Create,
    getInitialData: () => getCreateInitialData(fetchAllWithAcademic, schoolId),
    saveCallback: (data) => saveAcademic(data, create, schoolId),
    getButtonConfigs: getButtonConfigs,
  });

  const getViewConfig = (id): IWizardConfig => ({
    steps: [viewContentConfig],
    submitButtonText: null,
    cancelButtonText: "Close",
    mode: WizardMode.View,
    getInitialData: () => getViewData(fetchOne, id),
  });

  const getEditConfig = (id): IWizardConfig => ({
    steps: [editContentConfig],
    submitButtonText: "Save",
    submitButtonColor: SubmitButtonColor.Success,
    mode: WizardMode.Edit,
    getInitialData: () => getAcademicDataForEdit(fetchOne, id),
    saveCallback: (data) => saveEditedAcademic(data, update, schoolId, id),
  });

  const getActivateConfig = (id): IWizardConfig => ({
    steps: [
      {
        titleConfig: {
          titleText: "Activate academic",
        },
        content: "Are you sure you want to activate this academic?",
      },
    ],
    submitButtonText: "Activate",
    cancelButtonText: "Close",
    saveCallback: () => activate(id),
  });

  const getDeactivateConfig = (id): IWizardConfig => ({
    steps: [
      {
        titleConfig: {
          titleText: "Deactivate academic",
        },
        width: "552px",
        content: () => <DeactivateContent name={ENTITY_NAMES.academic} />,
        validator: deactivateFormValidator,
      },
    ],
    submitButtonText: "Deactivate",
    submitButtonColor: SubmitButtonColor.Danger,
    cancelButtonText: "Close",
    saveCallback: (data) =>
      deactivate(id, {
        text: data?.reason ?? "",
      }),
    getInitialData: () => getAppCount(fetchAppCount, id),
  });

  const getDeleteConfig = (id): IWizardConfig => ({
    steps: [
      {
        width: "430px",
        titleConfig: {
          titleText: "Delete academic",
        },
        content: () => <DeleteContent name={ENTITY_NAMES.academic} />,
      },
    ],
    mode: WizardMode.Delete,
    getInitialData: () => getAppCount(fetchAppCount, id),
    saveCallback: () => deleteOne(id),
    getButtonConfigs: getDeleteButtonConfigs,
  });

  return {
    getCreateConfig,
    getViewConfig,
    getEditConfig,
    getActivateConfig,
    getDeactivateConfig,
    getDeleteConfig,
  };
}
